import React, { useState, useEffect, useRef } from "react";
import Map, { Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import type { MapRef } from "react-map-gl";
import { motion } from "framer-motion";
import type { FillLayer } from "mapbox-gl";

const colors = ["#B0E5FF", "#32BEF0", "#2A86E3", "#4F65D6", "#29306A"];

interface DataLayers {
  municipios?: any;
  bairros?: any;
  setores?: any;
  centro?: { latitude: number; longitude: number };
}

interface Props {
  data: DataLayers;
  nivel: "municipios" | "bairros" | "setores";
  onNivelChange: (nivel: "municipios" | "bairros" | "setores") => void;
  initialViewState?: {
    longitude: number;
    latitude: number;
    zoom: number;
    bearing?: number;
    pitch?: number;
    padding?: { top: number; bottom: number; left: number; right: number };
  };
}

export default function MapaSocioeconomico({
  data,
  nivel,
  onNivelChange,
  initialViewState,
}: Props) {
  const mapRef = useRef<MapRef | any>(null);
  const [geojson, setGeojson] = useState<any | null>(null);
  const [intervalos, setIntervalos] = useState<number[]>([]);
  const [minMax, setMinMax] = useState<{ min: number; max: number } | null>(
    null
  );

  const defaultInitial = initialViewState ?? {
    longitude: -46.63,
    latitude: -23.55,
    zoom: 6,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  };

  const handleMapLoad = (event: any) => {
    const map = event.target;
    mapRef.current = map;

    // desativa scroll zoom padrão
    map.scrollZoom.disable();

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const zoomDelta = e.deltaY * -0.01;
        map.zoomTo(map.getZoom() + zoomDelta, { around: map.unproject([e.offsetX, e.offsetY]) });
      }
    };

    map.getCanvas().addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      map.getCanvas().removeEventListener('wheel', handleWheel);
    };
  };

  useEffect(() => {
    if (!mapRef.current) return;

    // Seleciona features conforme o nível
    const features =
      nivel === "bairros"
        ? data?.bairros
        : nivel === "setores"
        ? data?.setores
        : data?.municipios;

    if (!features || !Array.isArray(features) || features.length === 0) {
      setGeojson(null);
      setIntervalos([]);
      setMinMax(null);
      return;
    }

    // Cria GeoJSON
    const geo = { type: "FeatureCollection", features };
    setGeojson(geo);

    // Extrai valores válidos de densidade
    const valores = geo.features
      .map((f: any) => f.properties?.["densidade_demografica"])
      .filter((v: any) => typeof v === "number" && !isNaN(v));

    if (valores.length === 0) {
      setIntervalos([]);
      setMinMax(null);
      return;
    }

    const sorted = [...valores].sort((a, b) => a - b);
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    setMinMax({ min, max });

    // Calcula intervalos percentuais
    const limites = [0.2, 0.4, 0.6, 0.8]
      .map((q) => sorted[Math.floor(q * sorted.length)])
      .filter((v, i, arr) => v !== arr[i - 1]); // remove repetidos

    setIntervalos(limites);
  }, [data, nivel]);

  // Recentra mapa se houver centro
  useEffect(() => {
    if (!data?.centro) return;
    const { latitude, longitude } = data.centro;
    const mapInstance = mapRef.current?.getMap
      ? mapRef.current.getMap()
      : mapRef.current;
    if (!mapInstance) return;

    try {
      mapInstance.flyTo({
        center: [longitude, latitude],
        zoom: 12,
        speed: 0.8,
        curve: 1.4,
        essential: true,
      });
    } catch {
      mapInstance?.setCenter?.([longitude, latitude]);
      mapInstance?.setZoom?.(12);
    }
  }, [data?.centro]);

  // Cria fill layer para Mapbox
  const choroplethLayer: FillLayer = {
    id: "socio-layer",
    type: "fill",
    source: "socio",
    paint: {
      "fill-color": [
        "step",
        ["get", "densidade_demografica"],
        colors[0],
        ...intervalos.flatMap((val, i) => [val, colors[i + 1] ?? colors[colors.length - 1]]),
      ],
      "fill-opacity": 0.6,
      "fill-outline-color": "#ffffff",
    },
  };

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {/* Dropdown */}
      {geojson && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            zIndex: 10,
            background: "white",
            borderRadius: 6,
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            padding: "6px 8px",
          }}
        >
          <select
            value={nivel}
            onChange={(e) => onNivelChange(e.target.value as any)}
            style={{
              padding: "6px 8px",
              borderRadius: 4,
              border: "1px solid #ccc",
            }}
          >
            <option value="municipios">Municípios</option>
            <option value="bairros">Bairros</option>
            <option value="setores">Setores Censitários</option>
          </select>
        </motion.div>
      )}

      {/* Mapa */}
      <Map
        ref={mapRef}
        initialViewState={defaultInitial}
        mapboxAccessToken="pk.eyJ1IjoidGFjdGltb2JpIiwiYSI6ImNtZHQxZHRiYjB2b2oyam9oYWc4NTViMjQifQ.hbTysJxpk6TK0iIvuhasTA"
        mapStyle="mapbox://styles/mapbox/light-v10"
        style={{ width: "100%", height: "100%" }}
        onLoad={handleMapLoad}
      >
        {geojson && intervalos.length > 0 && (
          <Source
            key={`${nivel}-${geojson.features.length}`}
            id="socio"
            type="geojson"
            data={geojson}
          >
            <Layer {...choroplethLayer} />
          </Source>
        )}
      </Map>

      {/* Legenda */}
        {minMax && (
          <ul
            style={{
              position: "absolute",
              top: 70,
              right: 20,
              background: "white",
              padding: "10px 14px",
              borderRadius: 6,
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              margin: 0,
              listStyle: "none",
            }}
          >
            <h4 style={{ margin: "0 0 6px 0" }}>Densidade Demográfica</h4>
            {colors.slice(0, (intervalos.length || 1)).map((cor, i) => {
              const valorInferior = i === 0 ? minMax.min : intervalos[i - 1];
              const valorSuperior =
                i < intervalos.length ? intervalos[i] : minMax.max;

              return (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: 16,
                      height: 16,
                      marginRight: 6,
                      backgroundColor: cor,
                      border: "1px solid #333",
                    }}
                  />
                  {i === 0
                    ? `≤ ${valorSuperior.toLocaleString()} hab./km²`
                    : `${valorInferior?.toLocaleString()} – ${valorSuperior?.toLocaleString()} hab./km²`}
                </li>
              );
            })}
          </ul>
        )}
        {/* Botões de Zoom */}
          <div
            style={{
              position: "absolute",
              bottom: 20,
              right: 20,
              display: "flex",
              flexDirection: "column",
              gap: 5,
              zIndex: 10,
            }}
          >
            <button
              onClick={() => mapRef.current?.zoomIn()}
              style={{
                background: "white",
                border: "1px solid #ccc",
                borderRadius: "8px",
                width: "36px",
                height: "36px",
                fontSize: "20px",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              }}
            >
              +
            </button>
            <button
              onClick={() => mapRef.current?.zoomOut()}
              style={{
                background: "white",
                border: "1px solid #ccc",
                borderRadius: "8px",
                width: "36px",
                height: "36px",
                fontSize: "20px",
                cursor: "pointer",
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              }}
            >
              −
            </button>
          </div>
    </div>
  );
}
